import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable, when } from 'mobx';
import { Button, Icon, Intent, Spinner } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import FormDrawer from 'components/FormDrawer';
import ProductServiceModal from 'components/ProductsServices/ProductServiceModal';

const numeral = require('numeral');

// "Add labor" / "Add material" for a work order, estimate or invoice: search
// the price list and add items as lines, several in a row, from the keyboard
// (↓ ↑ to pick, Enter to add) or with a click. Each row says how many of that
// item the record already has; the footer keeps a tally of what this visit
// added. Thin wrappers per record: AddProductFor{WorkReport,Estimate,Invoice}Modal.
// Styles: styles/line-item-picker.scss (pick-).

// Wait this long after the last keystroke before searching.
const SEARCH_DELAY_MS = 350;
// How long a row shows its tick after an add.
const ADDED_FLASH_MS = 1600;

const LABOR = 'Service';

const price = (value) => (value === null || value === undefined || value === '' ? null : numeral(value).format('$0,0.00'));

@inject('viewStore', 'productServiceStore', 'invoiceStore', 'quoteStore', 'workReportStore') @observer
class LineItemPickerPanel extends Component {
  @observable active = -1;          // highlighted result (keyboard)
  @observable addingId = null;      // result being added
  @observable flashId = null;       // result that was just added
  @observable tally = [];           // [{ id, name, count }] added this visit
  @observable isNewItemOpen = false;

  searchInput = React.createRef();

  componentWillUnmount() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.flashTimer);
  }

  get search() {
    return this.props.viewStore.productServiceSearch;
  }

  get isLabor() {
    return this.props.type === LABOR;
  }

  get results() {
    return this.props.productServiceStore.productsServices;
  }

  // Is the record still saving (the add, or the update it triggers)?
  get recordBusy() {
    const { formType, invoiceStore, quoteStore, workReportStore } = this.props;
    if (formType === 'Invoice') return Boolean(invoiceStore.invoice.isLoading);
    if (formType === 'Quote') return Boolean(quoteStore.quote.isLoading);
    return Boolean(workReportStore.workReport.isLoading);
  }

  // Lines of this kind already on the record, by price list item.
  get onRecord() {
    const { record } = this.props;
    const lines = (record && record.attributes && record.attributes[this.isLabor ? 'labour' : 'materials']) || [];
    return lines.reduce((counts, line) => {
      if (line.product_service_id) counts[line.product_service_id] = (counts[line.product_service_id] || 0) + 1;
      return counts;
    }, {});
  }

  // Each opening starts clean: an empty search over this kind of item.
  handleOpened = () => {
    this.tally = [];
    this.active = -1;
    this.flashId = null;
    const { type, productServiceStore } = this.props;
    const stale = this.search.where.product_service_type !== type || (this.search.term && this.search.term !== '*');
    if (stale) {
      this.search.where.product_service_type = type;
      this.search.term = '*';
      this.search.page = 1;
      productServiceStore.loadProductAndServicesForWorkReports();
    }
    if (this.searchInput.current) this.searchInput.current.focus();
  };

  close = () => {
    clearTimeout(this.searchTimer);
    this.props.onClose();
  };

  runSearch = (page = 1) => {
    clearTimeout(this.searchTimer);
    this.pending = false;
    if (!this.search.term) this.search.term = '*';
    this.search.page = page;
    this.active = -1;
    this.props.productServiceStore.loadProductAndServicesForWorkReports();
  };

  onSearchChange = (event) => {
    this.search.term = event.target.value;
    this.active = -1;
    this.pending = true;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.runSearch(), SEARCH_DELAY_MS);
  };

  // ↓ ↑ move through the results; Enter adds the highlighted one (or searches
  // now when nothing is highlighted).
  onSearchKeyDown = (event) => {
    const count = (this.results.objects || []).length;
    if (event.key === 'ArrowDown' && count) {
      event.preventDefault();
      this.active = Math.min(this.active + 1, count - 1);
      this.scrollActiveIntoView();
    } else if (event.key === 'ArrowUp' && count) {
      event.preventDefault();
      this.active = Math.max(this.active - 1, -1);
      this.scrollActiveIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      // Enter right after typing searches; it adds only a row picked from
      // results that are already showing.
      const product = this.active >= 0 && !this.pending && !this.results.isLoading ? this.results.objects[this.active] : null;
      if (product) this.add(product);
      else this.runSearch();
    }
  };

  scrollActiveIntoView() {
    const element = document.getElementById(`pick-option-${this.active}`);
    if (element && element.scrollIntoView) element.scrollIntoView({ block: 'nearest' });
  }

  clearSearch = () => {
    this.search.term = '';
    this.runSearch();
    if (this.searchInput.current) this.searchInput.current.focus();
  };

  add(product) {
    if (this.addingId || this.recordBusy) return;
    const { formType, record, invoiceStore, quoteStore, workReportStore } = this.props;
    const kind = this.isLabor ? 'labour' : 'material';
    const store = formType === 'Invoice' ? invoiceStore : formType === 'Quote' ? quoteStore : workReportStore;
    this.addingId = product.id;
    store.addLineItem(product, record.id, kind)
      .then(() => {
        this.addingId = null;
        const name = product.attributes.name;
        const existing = this.tally.find((entry) => entry.id === product.id);
        if (existing) existing.count += 1;
        else this.tally.push({ id: product.id, name, count: 1 });
        this.flashId = product.id;
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => { this.flashId = null; }, ADDED_FLASH_MS);
      })
      .catch(() => {
        this.addingId = null;
        Toast.show({ message: `Couldn't add ${product.attributes.name}. Try again.`, intent: Intent.DANGER });
      });
  }

  addBlank = () => {
    const { onAddBlank } = this.props;
    onAddBlank()
      .then(() => {
        Toast.show({ message: this.isLabor ? 'Blank labor line added.' : 'Blank material line added.', intent: Intent.SUCCESS });
        this.close();
      })
      .catch(() => {
        Toast.show({ message: "Couldn't add a blank line. Try again.", intent: Intent.DANGER });
      });
  };

  openNewItem = (name) => {
    this.newItemName = name || null;
    if (name) this.props.productServiceStore.productService.object = { name };
    this.isNewItemOpen = true;
  };

  // A new price list item reloads the full price list into these results;
  // once that's done, bring back this panel's search (on the searched-for
  // name, when it was created from one).
  onNewItemCreated = () => {
    this.isNewItemOpen = false;
    const { productServiceStore } = this.props;
    const name = this.newItemName;
    when(
      () => !productServiceStore.productsServices.isLoading,
      () => {
        this.search.where.product_service_type = this.props.type;
        if (name) this.search.term = name;
        this.runSearch();
      }
    );
  };

  renderRow(product, index) {
    const attributes = product.attributes;
    const count = this.onRecord[product.id] || 0;
    const adding = this.addingId === product.id;
    const flash = this.flashId === product.id;
    const blocked = !adding && (Boolean(this.addingId) || this.recordBusy);
    const amount = price(attributes.unit_price);
    const noun = this.props.noun;
    return (
      <li
        key={product.id}
        id={`pick-option-${index}`}
        role="option"
        aria-selected={this.active === index}
        className={`pick-row${this.active === index ? ' is-active' : ''}${flash ? ' is-added' : ''}`}
        onMouseEnter={() => { this.active = index; }}
      >
        <div className="pick-row__main">
          <span className="pick-row__name">{attributes.name}</span>
          <span className="pick-row__meta">
            {!this.isLabor && attributes.part_number && <span className="pick-part">{attributes.part_number}</span>}
            {attributes.description
              ? <span className="pick-row__description">{attributes.description}</span>
              : <span className="pick-row__description is-empty">No description</span>}
          </span>
        </div>
        <div className="pick-row__side">
          <span className="pick-row__price">
            {amount || <span className="pick-row__no-price">No price</span>}
            {amount && this.isLabor && <span className="pick-row__unit">/hr</span>}
          </span>
          {count > 0 && <span className="pick-row__count">{count} on this {noun}</span>}
        </div>
        <button
          type="button"
          className={`pick-add${flash ? ' is-added' : ''}`}
          aria-label={flash ? `Added ${attributes.name}` : `Add ${attributes.name}`}
          aria-disabled={blocked ? true : undefined}
          tabIndex={-1}
          onClick={() => { if (!flash && !blocked) this.add(product); }}
        >
          {adding ? <Spinner size={14} /> : <Icon icon={flash ? 'tick' : 'plus'} iconSize={14} />}
        </button>
      </li>
    );
  }

  renderResults(term) {
    const { objects, isLoading, isError, meta } = this.results;
    const things = this.isLabor ? 'labor' : 'materials';
    const newText = this.isLabor ? 'New labor' : 'New material';
    if (isLoading) {
      return (
        <ul className="pick-list" aria-busy="true" aria-label="Loading">
          {[0, 1, 2, 3, 4].map((index) => (
            <li key={index} className="pick-row pick-row--bone" aria-hidden="true">
              <div className="pick-row__main">
                <span className="dl-bone" style={{ width: `${45 + (index % 3) * 12}%`, height: 14 }} />
                <span className="dl-bone" style={{ width: '70%', height: 10, marginTop: 8 }} />
              </div>
              <span className="dl-bone" style={{ width: 64, height: 14 }} />
              <span className="dl-bone" style={{ width: 32, height: 32, borderRadius: 999 }} />
            </li>
          ))}
        </ul>
      );
    }
    if (isError) {
      return (
        <div className="pick-empty" role="alert">
          <p className="pick-empty__title">The price list couldn't be loaded.</p>
          <p className="pick-empty__hint">Check your connection, then try again.</p>
          <Button icon="refresh" text="Try again" onClick={() => this.runSearch(this.search.page || 1)} />
        </div>
      );
    }
    if (!objects || objects.length === 0) {
      return (
        <div className="pick-empty" role="status">
          {term ? (
            <>
              <p className="pick-empty__title">No {things} match “{term}”.</p>
              <p className="pick-empty__hint">Check the spelling, or add it to your price list so it’s here next time.</p>
              <div className="pick-empty__actions">
                <Button icon="add" intent={Intent.PRIMARY} text={`Create “${term}”`} onClick={() => this.openNewItem(term)} />
                <Button minimal text="Clear search" onClick={this.clearSearch} />
              </div>
            </>
          ) : (
            <>
              <p className="pick-empty__title">No {things} in your price list yet.</p>
              <p className="pick-empty__hint">Add one to your price list, or add a blank line and type it in.</p>
              <div className="pick-empty__actions">
                <Button icon="add" intent={Intent.PRIMARY} text={newText} onClick={() => this.openNewItem()} />
              </div>
            </>
          )}
        </div>
      );
    }
    const page = (meta && meta.page) || 1;
    const pages = (meta && meta.total_pages) || 1;
    const total = meta && meta.total_records;
    return (
      <>
        <ul className="pick-list" role="listbox" id="pick-results" aria-label={this.isLabor ? 'Labor' : 'Materials'}>
          {objects.map((product, index) => this.renderRow(product, index))}
        </ul>
        {pages > 1 && (
          <nav className="pick-pages" aria-label="Pages">
            <span className="pick-pages__info">
              Page {page} of {pages}{total ? `, ${total} ${things}` : ''}
            </span>
            <Button minimal icon="chevron-left" text="Previous" disabled={page <= 1} onClick={() => this.runSearch(page - 1)} />
            <Button minimal rightIcon="chevron-right" text="Next" disabled={page >= pages} onClick={() => this.runSearch(page + 1)} />
          </nav>
        )}
      </>
    );
  }

  renderTally() {
    const { noun } = this.props;
    if (this.tally.length === 0) {
      return <span className="pick-tally__hint">Add as many as you need. They go on the {noun} as you add them.</span>;
    }
    const lines = this.tally.reduce((sum, entry) => sum + entry.count, 0);
    return (
      <div className="pick-tally__added" aria-live="polite">
        <span className="pick-tally__count">{lines} added</span>
        <ul className="pick-tally__chips">
          {this.tally.map((entry) => (
            <li key={entry.id} className="pick-chip">
              {entry.name}{entry.count > 1 && <span className="pick-chip__times">×{entry.count}</span>}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  render() {
    const { isOpen, blankBusy, itemType } = this.props;
    const term = this.search.term && this.search.term !== '*' ? this.search.term : '';
    const added = this.tally.length > 0;
    const activeId = this.active >= 0 ? `pick-option-${this.active}` : undefined;
    return (
      <>
        <FormDrawer
          title={this.isLabor ? 'Add labor' : 'Add material'}
          icon={this.isLabor ? 'wrench' : 'box'}
          tone="field"
          size="min(720px, 100vw)"
          className="pick"
          isOpen={isOpen}
          onOpened={this.handleOpened}
          onClose={this.close}
          busy={Boolean(blankBusy)}
          footer={
            <div className="pick-tally">
              {this.renderTally()}
              <div className="pick-tally__actions">
                <Button minimal icon="add-row-bottom" text="Blank line" loading={Boolean(blankBusy)} onClick={this.addBlank} />
                <Button text={added ? 'Done' : 'Close'} intent={added ? Intent.PRIMARY : Intent.NONE} onClick={this.close} disabled={Boolean(blankBusy)} />
              </div>
            </div>
          }
        >
          <div className="pick-search">
            <label className="sr-only" htmlFor="pick-search-input">{this.isLabor ? 'Find labor' : 'Find a material'}</label>
            <div className="pick-search__field">
              <Icon icon="search" iconSize={16} className="pick-search__icon" />
              <input
                id="pick-search-input"
                ref={this.searchInput}
                type="search"
                autoComplete="off"
                role="combobox"
                aria-expanded="true"
                aria-controls="pick-results"
                aria-activedescendant={activeId}
                aria-describedby="pick-search-help"
                placeholder={this.isLabor ? 'Search labor, e.g. Service call' : 'Search by name, part number or description'}
                value={term}
                onChange={this.onSearchChange}
                onKeyDown={this.onSearchKeyDown}
              />
              {term && (
                <button type="button" className="pick-search__clear" aria-label="Clear search" onClick={this.clearSearch}>
                  <Icon icon="cross" iconSize={14} />
                </button>
              )}
            </div>
            <Button className="pick-new" icon="add" text={this.isLabor ? 'New labor' : 'New material'} onClick={() => this.openNewItem(term)} />
          </div>
          <p className="pick-help" id="pick-search-help">
            <kbd>↓</kbd> <kbd>↑</kbd> to pick, <kbd>Enter</kbd> to add.
          </p>
          {this.renderResults(term)}
        </FormDrawer>
        <ProductServiceModal
          isOpen={this.isNewItemOpen}
          title={this.isLabor ? 'New labor' : 'New material'}
          edit={false}
          onClose={() => { this.isNewItemOpen = false; }}
          onSuccess={this.onNewItemCreated}
          itemType={itemType || (this.isLabor ? 'Labor' : 'Materials')}
        />
      </>
    );
  }
}

export default LineItemPickerPanel;
